import {
  getCommunityMember,
  getCommunityMembers,
  searchCreators,
  featureCreator,
  unfeatureCreator,
} from '@/api/community';
import sendMessage from '@/store/modules/community/sendMessage';
import section from '@/store/modules/community/section';
import { ISOCode, currencySymbol } from '@/utils/currencies';

export default {
  namespaced: true,
  modules: {
    sendMessage,
    section,
  },
  state: {
    members: [],
    creators: [],
    creatorsLoading: false,
    member: null,
    memberCount: null,
    memberLoading: false,
    content: [],
    contentLoading: false,
    featuredContent: [],
    featuredContentLoading: false,
    membersLoading: false,
    moreMembersLoading: false,
    page: 1,
    hasMoreMembers: true,
    featureCreatorLoading: false,
    involvedCreatorId: null,
  },
  mutations: {
    resetMemberProfile(state) {
      state.member = null;
      state.member = null;
      state.memberLoading = false;
      state.content = [];
      state.contentLoading = false;
      state.featuredContent = [];
      state.featuredContentLoading = false;
      state.hasMoreMembers = true;
      state.page = 1;
    },
    setMembers(state, members) {
      state.members = members;
    },
    setMoreMembersLoading(state, isLoading) {
      state.moreMembersLoading = isLoading;
    },
    appendMembers(state, members) {
      if (members.length === 0) state.hasMoreMembers = false;
      else {
        state.members = [...state.members, ...members];
        state.page += 1;
      }
    },
    resetPagination(state) {
      state.hasMoreMembers = true;
      state.page = 1;
    },
    setMember(state, member) {
      state.member = member;
    },
    updateMember(state, payload) {
      state.member = Object.assign(state.member, payload);
    },
    setMemberLoading(state, isLoading) {
      state.memberLoading = isLoading;
    },
    setContent(state, content) {
      state.content = content;
    },
    setFeaturedContent(state, content) {
      state.featuredContent = content;
    },
    setContentLoading(state, isLoading) {
      state.contentLoading = isLoading;
    },
    setFeaturedContentLoading(state, isLoading) {
      state.featuredContentLoading = isLoading;
    },
    setCreators(state, data) {
      state.creators = data;
    },
    setCreatorsLoading(state, isLoading) {
      state.creatorsLoading = isLoading;
    },
    setFeatureCreatorLoading(state, isLoading) {
      state.featureCreatorLoading = isLoading;
    },
    setInvolvedCreatorId(state, id) {
      state.involvedCreatorId = id;
    },
    updateCreator(state, { id, data }) {
      if (state.creators.length) {
        state.creators = state.creators.map((creator) => {
          if (creator.id === id) {
            return { ...creator, ...data };
          }
          return creator;
        });
      } else {
        state.members = state.members.map((creator) => {
          if (creator.id === id) {
            return { ...creator, ...data };
          }
          return creator;
        });
      }
    },
  },
  actions: {
    async loadMembers({ commit, dispatch }, type) {
      try {
        commit('setMemberLoading', true);
        const { data } = await getCommunityMembers(null, type);
        commit('setMembers', data);
      } catch (e) {
        dispatch('errorOverlay/setMessage', e, { root: true });
        throw e;
      } finally {
        commit('setMemberLoading', false);
      }
    },
    async loadMoreMembers({ commit, state }, type) {
      try {
        commit('setMoreMembersLoading', true);
        const { data } = await getCommunityMembers(state.page, type);
        commit('appendMembers', data);
      } finally {
        commit('setMoreMembersLoading', false);
      }
    },
    async loadMemberByUsername({ commit, dispatch }, username) {
      try {
        if (!username) {
          return;
        }

        commit('setMemberLoading', true);
        const { data } = await getCommunityMember(username);
        commit('setMember', data.user);
        await dispatch('profile/stripeData/setAccount', data.stripeAccount, { root: true });
        await dispatch('profile/subscriptionOptions/setPrices', data.prices, { root: true });
        await dispatch('profile/subscriptionOptions/setRequesterActiveSubscriptions', data.requesterActiveSubscriptions, { root: true });
        await dispatch('profile/subscriptionOptions/setSubscriptionState', data.subscribed, { root: true });
        await dispatch('profile/goalProgress/setGoalProgress', data.goalProgress, { root: true });
        commit('profile/subscriptionOptions/setSubscriptionSettings', data.subscriptionSettings, { root: true });
      } catch (e) {
        dispatch('errorOverlay/setMessage', e, { root: true });
        throw e;
      } finally {
        commit('setMemberLoading', false);
      }
    },
    async searchCreators({ commit, dispatch }, query) {
      try {
        commit('setCreatorsLoading', true);
        const { data } = await searchCreators(query);
        commit('setCreators', data);
      } catch (e) {
        dispatch('errorOverlay/setMessage', e, { root: true });
        throw e;
      } finally {
        commit('setCreatorsLoading', false);
      }
    },
    async featureCreator({ commit, dispatch }, id) {
      try {
        commit('setInvolvedCreatorId', id);
        commit('setFeatureCreatorLoading', true);
        const { data } = await featureCreator(id);
        commit('updateCreator', { id, data });
      } catch (e) {
        dispatch('errorOverlay/setMessage', e, { root: true });
      } finally {
        commit('setInvolvedCreatorId', null);
        commit('setFeatureCreatorLoading', false);
      }
    },
    async unfeatureCreator({ commit }, id) {
      try {
        commit('setInvolvedCreatorId', id);
        commit('setFeatureCreatorLoading', true);
        await unfeatureCreator(id);
        commit('updateCreator', { id, data: { featuredAt: null } });
      } catch (e) {
        console.error(e.response.data.message);
      } finally {
        commit('setInvolvedCreatorId', null);
        commit('setFeatureCreatorLoading', false);
      }
    },
  },
  getters: {
    member: state => state.member || {},
    members: state => state.members || [],
    memberLoading: state => state.memberLoading,
    moreMembersLoading: state => state.moreMembersLoading,
    content: state => state.content || [],
    contentLoading: state => state.contentLoading,
    creators: state => state.creators || [],
    featureCreatorLoading: state => state.featureCreatorLoading,
    involvedCreatorId: state => state.involvedCreatorId,
    memberCurrency: (state, getters) => getters.member.stripe?.currency || ISOCode.POUND_STERLING,
    memberCurrencySymbol: (state, getters) => currencySymbol[getters.memberCurrency],
  },
};
